export const GROUP_DROPPABLE_PREFIX = "groups:";

export const GROUP_REORDER_ERROR = {
  WRONG_PARENT: "לא ניתן להעביר מקבץ לקטגוריה אחרת",
  INVALID_SLOT: "לא ניתן למקם מקבץ במיקום זה",
  SAVE_FAILED: "עדכון סדר המקבצים נכשל",
};

export const SOLE_FAMILY_REORDER_HINT = "אין מקבצים נוספים לשינוי סדר";

export const isFamilyGroup = (node) => Boolean(node?.isFamily);

export const getReorderableFamilyNodes = (nodes = []) =>
  nodes.filter(isFamilyGroup);

export const isFamilyListReorderable = (familyCount) =>
  Number(familyCount) >= 2;

export const isCompleteGroupPositionPayload = (payload) => {
  if (!payload || typeof payload !== "object") return false;
  if (payload.kind === "prepend" || payload.kind === "append") return true;
  if (payload.kind === "betweenByRank") {
    return (
      payload.before != null &&
      payload.after != null &&
      payload.before !== "" &&
      payload.after !== ""
    );
  }
  return false;
};

export const getGroupDropError = ({
  sourceDroppableId,
  destinationDroppableId,
  fromIndex,
  toIndex,
  visibleCount,
}) => {
  if (
    sourceDroppableId &&
    destinationDroppableId &&
    sourceDroppableId !== destinationDroppableId
  ) {
    return GROUP_REORDER_ERROR.WRONG_PARENT;
  }
  if (
    !Number.isInteger(fromIndex) ||
    !Number.isInteger(toIndex) ||
    fromIndex < 0 ||
    toIndex < 0 ||
    !Number.isInteger(visibleCount) ||
    fromIndex >= visibleCount ||
    toIndex >= visibleCount
  ) {
    return GROUP_REORDER_ERROR.INVALID_SLOT;
  }
  return null;
};

export const resolveFamilyReorderFromListIndices = (
  childNodes,
  fromListIndex,
  toListIndex,
) => {
  if (
    !Array.isArray(childNodes) ||
    fromListIndex === toListIndex ||
    fromListIndex < 0 ||
    toListIndex < 0 ||
    fromListIndex >= childNodes.length ||
    toListIndex >= childNodes.length
  ) {
    return null;
  }

  const moved = childNodes[fromListIndex];
  if (!isFamilyGroup(moved)) return null;

  const familyNodes = getReorderableFamilyNodes(childNodes);
  const fromFamilyIndex = familyNodes.findIndex(
    (node) => node.slug === moved.slug,
  );
  if (fromFamilyIndex < 0) return null;

  const reorderedList = [...childNodes];
  const [removed] = reorderedList.splice(fromListIndex, 1);
  reorderedList.splice(toListIndex, 0, removed);

  const reorderedFamily = getReorderableFamilyNodes(reorderedList);
  const toFamilyIndex = reorderedFamily.findIndex(
    (node) => node.slug === moved.slug,
  );
  if (toFamilyIndex < 0) return null;

  return { fromFamilyIndex, toFamilyIndex };
};

export const getCategoryUpdateErrorMessage = (error) => {
  const data = error?.response?.data;
  if (typeof data === "string" && data.trim()) return data.trim();
  if (data && typeof data === "object") {
    for (const key of ["message", "error", "detail"]) {
      if (typeof data[key] === "string" && data[key].trim()) {
        return data[key].trim();
      }
    }
  }
  return GROUP_REORDER_ERROR.SAVE_FAILED;
};

export const makeGroupDroppableId = (parentSlug) =>
  `${GROUP_DROPPABLE_PREFIX}${parentSlug || "root"}`;

export const parseGroupDroppableId = (droppableId) => {
  if (
    !droppableId ||
    typeof droppableId !== "string" ||
    !droppableId.startsWith(GROUP_DROPPABLE_PREFIX)
  ) {
    return null;
  }
  return droppableId.slice(GROUP_DROPPABLE_PREFIX.length);
};

export const buildGroupPositionPayload = (items, destinationIndex) => {
  const total = items.length;
  if (destinationIndex === 0) return { kind: "prepend" };
  if (destinationIndex === total - 1) return { kind: "append" };

  const beforeItem = items[destinationIndex - 1];
  const afterItem = items[destinationIndex + 1];
  return {
    kind: "betweenByRank",
    before: beforeItem?.rank ?? beforeItem?.id,
    after: afterItem?.rank ?? afterItem?.id,
  };
};

export const findNodeBySlug = (node, slug) => {
  if (!node || !slug) return null;

  if (Array.isArray(node)) {
    for (const item of node) {
      const found = findNodeBySlug(item, slug);
      if (found) return found;
    }
    return null;
  }

  if (node.slug === slug) return node;

  for (const child of node.children || []) {
    const found = findNodeBySlug(child, slug);
    if (found) return found;
  }

  return null;
};

export const replaceChildrenBySlug = (node, targetSlug, nextChildren) => {
  if (!node || !targetSlug) return node;

  if (Array.isArray(node)) {
    return node.map((item) =>
      replaceChildrenBySlug(item, targetSlug, nextChildren),
    );
  }

  if (node.slug === targetSlug) {
    return {
      ...node,
      children: nextChildren,
    };
  }

  if (Array.isArray(node.children)) {
    return {
      ...node,
      children: node.children.map((child) =>
        replaceChildrenBySlug(child, targetSlug, nextChildren),
      ),
    };
  }

  return node;
};

export const permuteVisibleChildren = (
  allChildren,
  visibleNodes,
  fromIndex,
  toIndex,
) => {
  if (!Array.isArray(allChildren) || !Array.isArray(visibleNodes)) {
    return { nextChildren: allChildren, moved: null, reorderedVisible: [] };
  }
  if (
    fromIndex === toIndex ||
    fromIndex < 0 ||
    toIndex < 0 ||
    fromIndex >= visibleNodes.length ||
    toIndex >= visibleNodes.length
  ) {
    return {
      nextChildren: allChildren,
      moved: null,
      reorderedVisible: visibleNodes,
    };
  }

  const reorderedVisible = [...visibleNodes];
  const [moved] = reorderedVisible.splice(fromIndex, 1);
  reorderedVisible.splice(toIndex, 0, moved);

  const visibleSet = new Set(visibleNodes.map((node) => node?.slug));
  let visibleCursor = 0;
  const nextChildren = allChildren.map((child) =>
    visibleSet.has(child?.slug) ? reorderedVisible[visibleCursor++] : child,
  );

  return { nextChildren, moved, reorderedVisible };
};
